import type { Article } from './articles';

export const articlesStep05: Article[] = [
  {
    id: 42,
    step: '05',
    title: "【保存版】住宅契約前に必ず確認すべき10項目｜知らないと後悔するポイントまとめ",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://public.readdy.ai/ai/img_res/f9cba59e94c821b879f7b190b336d797.jpg",
    excerpt: "「契約前に何を確認すればいいのか分からない…」鳥取市・米子市・倉吉市で家づくりを始める方へ。追加費用のトラブルや後悔をほぼゼロにできる、契約前に必ず確認すべき10項目をわかりやすくまとめました。",
    tags: ["契約前", "10項目", "チェックリスト"],
    content: `
      <h2>はじめに</h2>
      <p>「契約前に何を確認すればいいのか分からない…」<br />「見積もりのどこを見れば"損しない"の？」<br />「契約したあとに追加費用が増えたらどうしよう…」</p>
      <p>鳥取市・米子市・倉吉市で家づくりを始める20〜30代の方から、こうした不安を本当に多く聞きます。私自身も初めての家づくりでは、契約前のチェックポイントが分からず「このまま契約して大丈夫？」と不安になった経験があります。</p>
      <p>しかし、契約前に確認すべきポイントを押さえておくだけで、<strong>追加費用のトラブルや後悔をほぼゼロにできる</strong>ようになります。</p>
      <p>この記事では、初心者でも迷わないように<strong>契約前に必ず確認すべき10項目</strong>をわかりやすくまとめました。</p>

      <h2>結論</h2>
      <p>住宅契約前に最も重要なのは、<br />「見積もりの範囲」「仕様の確定度」「追加費用の可能性」<br />この3つを明確にすることです。</p>
      <p>特に以下の10項目は、契約前に必ず確認すべきポイントです。</p>
      <ol>
        <li>見積もりの「含まれるもの・含まれないもの」</li>
        <li>付帯工事費の範囲</li>
        <li>地盤改良費の扱い</li>
        <li>外構工事の有無</li>
        <li>標準仕様の詳細</li>
        <li>オプション費用の一覧</li>
        <li>契約後に変更できる範囲</li>
        <li>工期と引き渡し時期</li>
        <li>保証・アフターサービス</li>
        <li>解約時の条件（違約金）</li>
      </ol>
      <p>これらを確認しておけば、契約後のトラブルはほぼ防げます。</p>

      <h2>① 見積もりの「含まれるもの・含まれないもの」</h2>
      <p>契約前に最も重要なのが、<strong>見積もりの範囲を明確にすること</strong>です。</p>
      <p>よくある"含まれないもの"は以下の通り。</p>
      <ul>
        <li>カーテン</li>
        <li>照明</li>
        <li>エアコン</li>
        <li>TVアンテナ</li>
        <li>網戸</li>
        <li>外構工事</li>
        <li>地盤改良</li>
      </ul>
      <p>これらが抜けていると、契約後に<strong>＋50〜150万円の追加費用</strong>が発生します。</p>

      <h2>② 付帯工事費の範囲</h2>
      <p>付帯工事費は、家を建てるために必要な"外側の工事"です。含まれるべき項目は以下。</p>
      <ul>
        <li>外部給排水工事</li>
        <li>電気引き込み</li>
        <li>ガス工事</li>
        <li>仮設工事（足場・仮設トイレ）</li>
      </ul>
      <p>鳥取市・米子市は外部配管が長くなる土地も多く、<strong>付帯工事費が高くなりやすい</strong>ため要注意です。</p>

      <h2>③ 地盤改良費の扱い</h2>
      <p>地盤改良は、契約後に追加されることが多い項目です。確認すべきポイントは以下。</p>
      <ul>
        <li>地盤改良費は見積もりに含まれているか</li>
        <li>「別途」と書かれていないか</li>
        <li>改良が必要な場合の費用目安（50〜150万円）</li>
      </ul>
      <p>鳥取県は地盤が弱いエリアもあるため、<strong>地盤改良は高確率で必要</strong>と考えておくべきです。</p>

      <h2>④ 外構工事の有無</h2>
      <p>外構は後回しにされがちですが、<strong>生活に直結する重要な工事</strong>です。</p>
      <p>外構に含まれるもの</p>
      <ul>
        <li>駐車場コンクリート</li>
        <li>フェンス</li>
        <li>砂利敷き</li>
        <li>アプローチ</li>
      </ul>
      <p>外構が見積もりに入っていない場合、<strong>＋80〜150万円</strong>は覚悟が必要です。</p>

      <h2>⑤ 標準仕様の詳細</h2>
      <p>標準仕様は住宅会社によって大きく違います。確認すべきポイント</p>
      <ul>
        <li>断熱材の種類・厚み</li>
        <li>サッシの種類（樹脂 or アルミ樹脂複合）</li>
        <li>キッチン・お風呂のグレード</li>
        <li>床材の種類</li>
      </ul>
      <p>鳥取の冬は寒いため、<strong>断熱性能は特に重要</strong>です。</p>

      <h2>⑥ オプション費用の一覧</h2>
      <p>契約後に「これはオプションです」と言われると、<strong>追加費用が一気に増えます</strong>。特に注意すべきオプション</p>
      <ul>
        <li>キッチンのグレードアップ</li>
        <li>収納の追加</li>
        <li>照明・コンセント</li>
        <li>造作家具</li>
        <li>トイレのグレード</li>
      </ul>
      <p>オプション費用は契約前に必ず一覧で確認しましょう。</p>

      <h2>⑦ 契約後に変更できる範囲</h2>
      <p>契約後に変更できる範囲は会社によって違います。例</p>
      <ul>
        <li>間取り変更は契約後NG</li>
        <li>設備の変更は着工前までOK</li>
        <li>外構は引き渡し前まで変更可能</li>
      </ul>
      <p>「どこまで変更できるか」を知らないと、<strong>後から後悔する原因</strong>になります。</p>

      <h2>⑧ 工期と引き渡し時期</h2>
      <p>工期は家づくりのスケジュールに直結します。確認すべきポイント</p>
      <ul>
        <li>着工から引き渡しまでの期間</li>
        <li>遅延した場合の対応</li>
        <li>引っ越し時期の調整</li>
      </ul>
      <p>鳥取県は冬季に工事が遅れやすいため、<strong>冬の工期は特に注意</strong>が必要です。</p>

      <h2>⑨ 保証・アフターサービス</h2>
      <p>保証内容は住宅会社によって大きく違います。確認すべき項目</p>
      <ul>
        <li>構造保証（10年）</li>
        <li>防水保証（10年）</li>
        <li>設備保証（1〜10年）</li>
        <li>定期点検の頻度</li>
      </ul>
      <p>アフターが弱い会社は、<strong>住んでからのトラブル対応が遅くなりがち</strong>です。</p>

      <h2>⑩ 解約時の条件（違約金）</h2>
      <p>契約後に「やっぱり別の会社にしたい」と思っても、<strong>違約金が高額だと動けません</strong>。確認すべきポイント</p>
      <ul>
        <li>解約できるタイミング</li>
        <li>違約金の金額</li>
        <li>返金される費用の範囲</li>
      </ul>
      <p>契約書の中でも最重要項目です。</p>

      <h2>まとめ</h2>
      <p>住宅契約前に確認すべき10項目は以下の通り。</p>
      <ul>
        <li>見積もりの範囲</li>
        <li>付帯工事費</li>
        <li>地盤改良</li>
        <li>外構</li>
        <li>標準仕様</li>
        <li>オプション</li>
        <li>変更できる範囲</li>
        <li>工期</li>
        <li>保証</li>
        <li>解約条件</li>
      </ul>
      <p>これらを確認しておけば、<strong>契約後のトラブルはほぼゼロにできます</strong>。</p>

      <h2>次に読む記事</h2>
      <p>契約前の注意点が分かったら、次に知りたいのは「契約の流れ」だと思います。契約当日に何をするのか、どんな書類にサインするのかを知っておくと、不安が一気に減ります。</p>
      <p>👉 次の記事：<a href="/articles/43">初心者でもわかる！住宅会社との契約の流れ｜重要書類・注意点・トラブル回避ガイド</a></p>
    `
  },
  {
    id: 43,
    step: '05',
    title: "初心者でもわかる！住宅会社との契約の流れ｜重要書類・注意点・トラブル回避ガイド",
    category: "契約・引き渡し",
    date: "2026年3月28日",
    readTime: "約16分",
    image: "https://readdy.ai/api/search-image?query=A%20warm%20professional%20scene%20of%20a%20Japanese%20housing%20contract%20signing%20process%20in%20a%20modern%20bright%20office%2C%20documents%20spread%20on%20a%20clean%20white%20desk%20with%20a%20pen%20and%20small%20house%20model%2C%20natural%20light%20streaming%20in%20through%20large%20windows%2C%20calm%20and%20reassuring%20atmosphere%2C%20warm%20earthy%20tones%20with%20white%20accents%2C%20trustworthy%20and%20confident%20real%20estate%20setting%2C%20no%20people%20visible%2C%20minimalist%20Japanese%20design&width=600&height=400&seq=art043step05&orientation=landscape",
    excerpt: "住宅会社との契約は家づくりの中でも最も緊張する場面のひとつ。「契約って何をするの？」「どんな書類にサインするの？」鳥取市・米子市・倉吉市で悩む方へ、契約の流れ・必要書類・注意点・トラブル回避のコツをわかりやすく解説します。",
    tags: ["契約の流れ", "重要書類", "トラブル回避"],
    content: `
      <h2>はじめに</h2>
      <p>住宅会社との契約は、家づくりの中でも最も緊張する場面のひとつです。「契約って何をするの？」「どんな書類にサインするの？」「契約後に変更できるの？」と不安を抱える方は、鳥取市・米子市・倉吉市でもとても多いです。</p>
      <p>私自身も初めての家づくりでは、契約の流れが分からず「このまま進めて大丈夫なのかな…」と不安になった経験があります。ですが、契約の流れと注意点を理解しておくだけで、安心して家づくりを進められるようになります。</p>
      <p>この記事では、初心者でも迷わないように<strong>住宅会社との契約の流れ・必要書類・注意点・トラブル回避のコツ</strong>をわかりやすくまとめました。</p>

      <h2>結論</h2>
      <p>住宅会社との契約は、<br />①見積もり確定 → ②契約書の説明 → ③重要事項説明 → ④契約締結（署名・押印） → ⑤手付金支払い<br />という流れで進みます。</p>
      <p>そして最も重要なのは、<strong>契約前に「不明点ゼロ」の状態にしておくこと</strong>。契約後は変更できない項目も多いため、疑問や不安は必ず事前に解消しておく必要があります。</p>

      <h2>契約の流れをステップごとに解説</h2>

      <h3>1. 見積もり・プランの最終確認</h3>
      <p>契約前に、まずは<strong>間取り・仕様・見積もりの最終確認</strong>を行います。確認すべきポイント</p>
      <ul>
        <li>間取りは本当にこれで良いか</li>
        <li>標準仕様とオプションの境界</li>
        <li>見積もりに抜け漏れがないか</li>
        <li>外構・地盤改良・付帯工事の扱い</li>
      </ul>
      <p>鳥取県では地盤改良や外構費が追加になりやすいため、ここで曖昧な部分を残すと後悔につながります。</p>

      <h3>2. 契約書の説明（重要ポイントの確認）</h3>
      <p>住宅会社から「契約書」の説明があります。ここが最も重要なステップです。契約書で必ず確認すべき項目</p>
      <ul>
        <li>工事請負契約の内容</li>
        <li>工期（着工〜引き渡し）</li>
        <li>支払いスケジュール</li>
        <li>追加費用が発生する条件</li>
        <li>解約時の違約金</li>
        <li>保証内容（構造・防水など）</li>
      </ul>
      <p>特に「追加費用が発生する条件」は必ず確認してください。曖昧なまま契約すると、後から数十万円の追加が発生することがあります。</p>

      <h3>3. 重要事項説明（宅建士による説明）</h3>
      <p>宅地建物取引士が、<strong>土地・建物に関する重要事項</strong>を説明します。説明される内容</p>
      <ul>
        <li>土地の権利関係</li>
        <li>建築条件</li>
        <li>法規制（建ぺい率・容積率）</li>
        <li>インフラ（上下水道・電気）</li>
        <li>地盤・ハザード情報</li>
      </ul>
      <p>鳥取市・米子市は水害・地震のリスクが地域によって異なるため、<strong>ハザードマップの説明は特に重要</strong>です。</p>

      <h3>4. 契約締結（署名・押印）</h3>
      <p>説明が終わったら、<strong>契約書に署名・押印</strong>を行います。このタイミングで</p>
      <ul>
        <li>工事請負契約書</li>
        <li>見積書</li>
        <li>設計図書</li>
        <li>仕様書</li>
      </ul>
      <p>などにサインします。署名後は「契約成立」となり、<strong>契約後の大幅な変更は難しくなる</strong>ため注意が必要です。</p>

      <h3>5. 手付金の支払い</h3>
      <p>契約後、数日以内に<strong>手付金（10〜50万円程度）</strong>を支払います。手付金は</p>
      <ul>
        <li>契約の証明</li>
        <li>工事準備の着手金</li>
      </ul>
      <p>として扱われます。※金額は会社によって異なります。</p>

      <h2>契約時の注意点（初心者が後悔しやすいポイント）</h2>
      <ol>
        <li>契約後に間取り変更できないケースがある → 契約前に間取りは必ず確定させる。</li>
        <li>見積もりの「別途工事」に注意 → 外構・地盤改良・照明・カーテンは要チェック。</li>
        <li>工期の遅れはよくある → 冬の鳥取は雪で遅れやすい。</li>
        <li>契約後のキャンセルは違約金が発生 → 契約前に不安はすべて解消しておく。</li>
        <li>契約書は持ち帰って確認してもOK → その場で急かされても焦らない。</li>
      </ol>

      <h2>契約でトラブルを避けるコツ</h2>
      <ul>
        <li>不明点は必ず質問する</li>
        <li>契約書のコピーを事前にもらう</li>
        <li>追加費用の可能性を確認する</li>
        <li>口頭説明は必ず書面に残す</li>
        <li>他社の見積もりと比較する</li>
      </ul>
      <p>特に「口頭での約束」は後からトラブルになりやすいため、<strong>必ず書面に残すことが大切</strong>です。</p>

      <h2>まとめ</h2>
      <p>住宅会社との契約は、<br />見積もり確定 → 契約書説明 → 重要事項説明 → 契約締結 → 手付金支払い<br />という流れで進みます。</p>
      <p>契約前に不明点をゼロにしておくことで、<strong>後悔やトラブルを大幅に減らすことができます</strong>。</p>

      <h2>次に読む記事</h2>
      <p>契約の流れが分かったら、次に気になるのは「契約後に何をすればいいのか？」という点だと思います。契約後は、色決め・仕様決め・住宅ローン本審査など、やることが一気に増えます。</p>
      <p>👉 次の記事：<a href="/articles/44">【契約後にやることリスト】住宅ローン本審査・色決め・仕様決め・外構計画の完全ガイド</a></p>
    `
  }
];
